import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { EXTENSION_URL_SCHEMES, IGNORED_ERRORS } from './constants';

/**
 * Whether or not application is running in local dev
 */
const isLocal =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';

function isExtensionError(stack: string | undefined) {
  return EXTENSION_URL_SCHEMES.some((scheme) => stack?.includes(scheme));
}

/**
 * Whether a RUM event should be ignored.
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
 */
export function shouldIgnoreEvent(event: RumEvent) {
  if (event.type !== 'error') {
    return false;
  }

  if (isExtensionError(event.error.stack)) {
    return true;
  }

  return IGNORED_ERRORS.some((substring) =>
    event.error.message.includes(substring),
  );
}

export function initDatadog() {
  // These will log errors if missing env vars and be picked up by Datadog RUM
  if (!process.env.NEXT_PUBLIC_APP_VERSION) {
    // biome-ignore lint/suspicious/noConsole: Used for debugging
    console.error('[Side RUM]: Missing APP_VERSION variable');
  }

  if (!process.env.NEXT_PUBLIC_APP_ENV) {
    // biome-ignore lint/suspicious/noConsole: Used for debugging
    console.error('[Side RUM]: Missing APP_ENV variable');
  }

  datadogRum.init({
    clientToken: 'pube7a7f8e72796ed6374b57cfe9fd5989b', // cspell:disable-line
    env: process.env.NEXT_PUBLIC_APP_ENV,
    allowedTracingUrls: [
      {
        match: (url: string) => {
          const { origin, pathname } = new URL(url);
          if (origin === process.env.NEXT_PUBLIC_CLOUD_FUNCTION) {
            return true;
          }
          return (
            // Current origin
            origin === window?.location.origin &&
            // Proxy paths
            pathname.startsWith(
              `${process.env.NEXT_PUBLIC_BASE_PATH}/api/proxies/`,
            )
          );
        },
        propagatorTypes: ['datadog', 'tracecontext'], // cspell:disable-line
      },
    ],
    applicationId: '86c5e725-3b38-43a5-b060-e7e67a63396c',
    compressIntakeRequests: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Feature flag usage tracking
    // NOTE: Inspector is enabled in src/modules/launch-darkly/provider.tsx
    enableExperimentalFeatures: ['feature_flags'],
    service: 'transactions-management-ui',
    sessionReplaySampleRate: 0, // Not currently using replays
    // disable RUM on the test environment and in local dev
    sessionSampleRate:
      process.env.NEXT_PUBLIC_APP_ENV === 'test' || isLocal ? 0 : 100,
    telemetrySampleRate:
      process.env.NEXT_PUBLIC_APP_ENV === 'test' || isLocal ? 0 : 100,
    traceSampleRate:
      process.env.NEXT_PUBLIC_APP_ENV === 'test' || isLocal ? 0 : 30,
    site: 'datadoghq.com',
    trackUserInteractions: true,
    version: process.env.NEXT_PUBLIC_APP_VERSION,
    beforeSend: (event) => !shouldIgnoreEvent(event),
  });
}
